/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { TGenericModelNode } from "@adapptio/lib-blueprint";
import React, { FC, useMemo } from "react";
import { IFieldValidationError } from "../Fields/FieldTypes";
import { IInspectorControlConfig, IInspectorControls } from "./InspectorTypes";

export interface IInspectorControlsProps<TModel extends TGenericModelNode = TGenericModelNode> {
	config: IInspectorControlConfig<TModel>;
	path: string[];
	depth: number;
	overrideControls?: Partial<IInspectorControls>;
	additionalValidationErrors?: IFieldValidationError[];
	id?: string;
	label?: string;
}

/**
 * Renders inspector control based on config
 *
 * @param Component props
 */
export const InspectorControl: FC<IInspectorControlsProps> = ({
	config,
	path,
	depth,
	overrideControls,
	additionalValidationErrors,
	id,
	label
}) => {
	return useMemo(
		() => (
			<config.component
				model={config.model}
				path={path}
				depth={depth}
				overrideControls={overrideControls}
				additionalValidationErrors={additionalValidationErrors}
				id={id}
				label={label}
			/>
		),
		[
			config.component,
			config.model,
			path,
			depth,
			overrideControls,
			JSON.stringify(additionalValidationErrors)
		]
	);
};
